import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { UserSettingsStateService } from '@services/user-settings.state.service';
import {
  API_URL,
  APP_CONFIG,
  ConfigSettings,
  THEME,
  THEMES,
} from '@shared/config';
import {
  ThemeMemoryOnlyValues,
  ThemeMemoryValues,
} from '@shared/config/branding.config';
import { defaultThemeValues } from '@shared/config/default-theme.config';
import {
  FONT_FROM_COMPANY,
  FONT_FROM_COMPANY_DEFAULT,
} from '@shared/config/fonts.config';
import { Branding } from '@shared/models';
import {
  BrandingCustomization,
  buildCustomizationValuesObject,
} from '@shared/models/branding-customization-value.model';
import { DEFAULT_PBI_THEME } from '@shared/models/branding.model';
import { isEmpty } from 'lodash-es';
import { Observable, combineLatest, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Injectable()
export class BrandingService {
  private readonly http = inject(HttpClient);
  private readonly userSettingsStateService = inject(UserSettingsStateService);

  globalSettings: ConfigSettings = inject(APP_CONFIG);
  static POWERBI_THEME;

  getBrandingByThemeName(themeName: string): Observable<Branding> {
    const branding$ = this.http.get<Branding>(
      `${this.globalSettings.orApiUrl}${API_URL.branding}/${themeName}`,
      { withCredentials: true }
    );

    const brandingCustomization$ = this.http.get<BrandingCustomization[]>(
      `${this.globalSettings.orApiUrl}${API_URL.brandingCustomizations}`,
      { withCredentials: true }
    );

    return combineLatest([branding$, brandingCustomization$]).pipe(
      switchMap(result => {
        const [branding, brandingThemeCustomizations] = result;

        return of({
          ...branding,
          customizationValues: buildCustomizationValuesObject(
            brandingThemeCustomizations,
            branding.customizationValues
          ),
        });
      })
    );
  }

  nextTheme(currentTheme: THEMES): THEMES {
    return currentTheme === THEMES.dark ? THEMES.light : THEMES.dark;
  }

  loadThemeValues(branding: Branding, userSettingsId?: number) {
    if (!branding) {
      return;
    }
    if (userSettingsId) {
      this.userSettingsStateService.setUserSettingsId(userSettingsId);
    }

    const { theme } = branding;

    if (!isEmpty(branding.customizationValues)) {
      this.setMemoryAndCssVariables(branding.customizationValues, theme);
    }
    this.loadPowerBITheme(branding);
    this.userSettingsStateService.setTheme(theme);
  }

  private setMemoryAndCssVariables(
    customizationValues: any,
    themeName: string
  ) {
    const memoryVariables = Object.values(ThemeMemoryValues);

    // Load into memory only the values that are really needed
    memoryVariables.forEach(variable => {
      THEME[themeName][variable] = customizationValues[variable];
    });

    // Removes all properties from the initialValue object that also exist
    // in the object on which we call reduce.
    const reducer = (remainingVariables, variableToIgnore: string) => {
      const { [variableToIgnore]: _, ...updatedObject } = remainingVariables;
      return updatedObject;
    };

    const memoryOnlyVariables = Object.values(ThemeMemoryOnlyValues);

    // Remove all properties that are only for in-memory
    const cssVariables = memoryOnlyVariables.reduce(
      reducer,
      customizationValues
    );

    this.loadCssVariables(cssVariables);
  }

  public loadDefaultCssVariables(): void {
    this.loadCssVariables(defaultThemeValues);
  }

  private loadCssVariables(themeVariables: Record<string, string>): void {
    const documentStyle = document.documentElement.style;
    const setCssVariable = ([key, property]: [string, string]) => {
      documentStyle.setProperty(`--${key}`, property);
    };
    themeVariables['font-family'] =
      FONT_FROM_COMPANY[this.globalSettings.company] ??
      FONT_FROM_COMPANY_DEFAULT;
    Object.entries(themeVariables).forEach(setCssVariable);
    this.setToastrContrastVariables(themeVariables, documentStyle);
  }

  private setToastrContrastVariables(
    themeVariables: Record<string, string>,
    documentStyle: CSSStyleDeclaration
  ): void {
    const computedStyles = getComputedStyle(document.documentElement);
    const existingBackground =
      themeVariables['toastr-background-color']?.trim() ||
      computedStyles.getPropertyValue('--toastr-background-color').trim();
    const background = existingBackground || '#111827';
    if (!existingBackground) {
      documentStyle.setProperty('--toastr-background-color', background);
    }

    const existingDefault =
      themeVariables['toastr-default']?.trim() ||
      computedStyles.getPropertyValue('--toastr-default').trim();
    if (!existingDefault) {
      documentStyle.setProperty(
        '--toastr-default',
        this.getContrastTextColor(background)
      );
    }
  }

  private getContrastTextColor(color: string): string {
    const rgb = this.parseColor(color);
    if (!rgb) {
      return '#f8fafc';
    }

    const [r, g, b] = rgb.map(value => value / 255);
    const luminance =
      0.2126 * this.srgbToLinear(r) +
      0.7152 * this.srgbToLinear(g) +
      0.0722 * this.srgbToLinear(b);

    return luminance > 0.5 ? '#0b1224' : '#f8fafc';
  }

  private srgbToLinear(channel: number): number {
    return channel <= 0.03928
      ? channel / 12.92
      : Math.pow((channel + 0.055) / 1.055, 2.4);
  }

  private parseColor(color: string): [number, number, number] | null {
    const trimmed = color.trim();
    if (!trimmed) {
      return null;
    }

    if (trimmed.startsWith('#')) {
      const hex = trimmed.slice(1);
      if (hex.length === 3) {
        const r = parseInt(hex[0] + hex[0], 16);
        const g = parseInt(hex[1] + hex[1], 16);
        const b = parseInt(hex[2] + hex[2], 16);
        return [r, g, b];
      }

      if (hex.length === 6 || hex.length === 8) {
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return [r, g, b];
      }
    }

    const rgbMatch = trimmed.match(/^rgba?\(([^)]+)\)$/i);
    if (!rgbMatch) {
      return null;
    }

    const parts = rgbMatch[1]
      .split(',')
      .map(part => part.trim())
      .filter(Boolean);
    if (parts.length < 3) {
      return null;
    }

    const r = this.parseRgbChannel(parts[0]);
    const g = this.parseRgbChannel(parts[1]);
    const b = this.parseRgbChannel(parts[2]);

    if (r === null || g === null || b === null) {
      return null;
    }

    return [r, g, b];
  }

  private parseRgbChannel(value: string): number | null {
    if (value.endsWith('%')) {
      const percent = Number.parseFloat(value);
      if (Number.isNaN(percent)) {
        return null;
      }
      return Math.max(0, Math.min(255, Math.round((percent / 100) * 255)));
    }

    const numeric = Number.parseFloat(value);
    if (Number.isNaN(numeric)) {
      return null;
    }

    return Math.max(0, Math.min(255, Math.round(numeric)));
  }

  loadPowerBITheme(branding: Branding): void {
    if (branding.powerBIThemes[DEFAULT_PBI_THEME]) {
      BrandingService.POWERBI_THEME = JSON.parse(
        branding.powerBIThemes[DEFAULT_PBI_THEME].themeFile
      );
    }
  }
}
